import { useState, useEffect } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Upload,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { getCategories } from "@/lib/api";
import { getToken } from "@/lib/auth";
import { createErrorMessage } from "@/lib/error-message";
import type { Category } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_main/exams/$slug/admin/questions/bulk")({
  component: AdminBulkCreatePage,
});

const API_BASE =
  import.meta.env.VITE_API_URL || "http://localhost:8080/api/v1";

function adminFetch(endpoint: string, options: RequestInit = {}) {
  const token = getToken();
  if (!token) {
    return Promise.reject(new Error("로그인이 필요합니다"));
  }
  return fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...((options.headers as Record<string, string>) ?? {}),
    },
  });
}

interface ChoiceInput {
  label: string;
  text: string;
  is_correct: boolean;
}

interface QuestionInput {
  question_number: string;
  text_ko: string;
  is_multiple: boolean;
  category_id: string;
  choices: ChoiceInput[];
  collapsed: boolean;
}

const LABELS = ["A", "B", "C", "D", "E"];

function createEmptyQuestion(): QuestionInput {
  return {
    question_number: "",
    text_ko: "",
    is_multiple: false,
    category_id: "",
    choices: [
      { label: "A", text: "", is_correct: false },
      { label: "B", text: "", is_correct: false },
      { label: "C", text: "", is_correct: false },
      { label: "D", text: "", is_correct: false },
    ],
    collapsed: false,
  };
}

function AdminBulkCreatePage() {
  const navigate = useNavigate();
  const { slug: examSlug } = Route.useParams();
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();

  const [categories, setCategories] = useState<Category[]>([]);
  const [questions, setQuestions] = useState<QuestionInput[]>([
    createEmptyQuestion(),
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    created: number;
    failed: number;
    errors?: string[];
  } | null>(null);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== "admin")) {
      navigate({ to: "/dashboard", replace: true });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!examSlug) return;
    getCategories(examSlug)
      .then(setCategories)
      .catch(() => {});
  }, [examSlug]);

  function addQuestion() {
    setQuestions((prev) => [...prev, createEmptyQuestion()]);
  }

  function removeQuestion(index: number) {
    if (questions.length <= 1) return;
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  }

  function updateQuestion(
    index: number,
    field: keyof QuestionInput,
    value: string | boolean
  ) {
    setQuestions((prev) =>
      prev.map((q, i) => (i === index ? { ...q, [field]: value } : q))
    );
  }

  function updateChoice(
    qIndex: number,
    cIndex: number,
    field: keyof ChoiceInput,
    value: string | boolean
  ) {
    setQuestions((prev) =>
      prev.map((q, qi) =>
        qi === qIndex
          ? {
              ...q,
              choices: q.choices.map((c, ci) =>
                ci === cIndex ? { ...c, [field]: value } : c
              ),
            }
          : q
      )
    );
  }

  function addChoice(qIndex: number) {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIndex || q.choices.length >= 5) return q;
        return {
          ...q,
          choices: [
            ...q.choices,
            { label: LABELS[q.choices.length], text: "", is_correct: false },
          ],
        };
      })
    );
  }

  function removeChoice(qIndex: number) {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIndex || q.choices.length <= 2) return q;
        return { ...q, choices: q.choices.slice(0, -1) };
      })
    );
  }

  function toggleCollapse(index: number) {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === index ? { ...q, collapsed: !q.collapsed } : q
      )
    );
  }

  function handleJsonImport() {
    const input = prompt(
      "JSON 배열을 붙여넣으세요.\n\n예시:\n" +
        JSON.stringify(
          [
            {
              question_number: 1,
              text_ko: "문제 텍스트",
              is_multiple: false,
              choices: [
                { label: "A", text: "선택지 A", is_correct: true },
                { label: "B", text: "선택지 B", is_correct: false },
                { label: "C", text: "선택지 C", is_correct: false },
                { label: "D", text: "선택지 D", is_correct: false },
              ],
            },
          ],
          null,
          2
        )
    );

    if (!input) return;

    try {
      const parsed = JSON.parse(input);
      if (!Array.isArray(parsed)) {
        setError("JSON 배열 형식이어야 합니다");
        return;
      }

      const imported: QuestionInput[] = parsed.map(
        (q: Record<string, unknown>) => ({
          question_number: String(q.question_number ?? ""),
          text_ko: String(q.text_ko ?? ""),
          is_multiple: Boolean(q.is_multiple),
          category_id: q.category_id ? String(q.category_id) : "",
          choices: Array.isArray(q.choices)
            ? (q.choices as Record<string, unknown>[]).map((c) => ({
                label: String(c.label ?? ""),
                text: String(c.text ?? ""),
                is_correct: Boolean(c.is_correct),
              }))
            : [
                { label: "A", text: "", is_correct: false },
                { label: "B", text: "", is_correct: false },
                { label: "C", text: "", is_correct: false },
                { label: "D", text: "", is_correct: false },
              ],
          collapsed: false,
        })
      );

      setQuestions(imported);
      setError(null);
      setResult(null);
    } catch {
      setError("유효하지 않은 JSON 형식입니다");
    }
  }

  async function handleSubmit() {
    setError(null);
    setResult(null);

    // Validate
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.question_number || !q.text_ko) {
        setError(`${i + 1}번째 문제: 문제 번호와 텍스트는 필수입니다`);
        return;
      }
      if (!q.choices.some((c) => c.is_correct)) {
        setError(`${i + 1}번째 문제: 정답을 최소 1개 이상 선택해 주세요`);
        return;
      }
      if (q.choices.some((c) => !c.text.trim())) {
        setError(`${i + 1}번째 문제: 모든 선택지의 텍스트를 입력해 주세요`);
        return;
      }
    }

    setSubmitting(true);

    try {
      const body = {
        questions: questions.map((q) => {
          const item: Record<string, unknown> = {
            question_number: Number(q.question_number),
            text_ko: q.text_ko,
            is_multiple: q.is_multiple,
            choices: q.choices.map((c) => ({
              label: c.label,
              text: c.text,
              is_correct: c.is_correct,
            })),
          };
          if (q.category_id) item.category_id = Number(q.category_id);
          return item;
        }),
      };

      const res = await adminFetch(`/admin/exams/${examSlug}/questions/bulk`, {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || "일괄 등록에 실패했습니다");
      }

      const data = await res.json();
      setResult(data.data);
    } catch (err) {
      setError(createErrorMessage(err, "일괄 등록에 실패했습니다"));
    } finally {
      setSubmitting(false);
    }
  }

  if (authLoading || !user || user.role !== "admin") {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/exams/$slug/admin/questions"
            params={{ slug: examSlug }}
            className="mb-4 inline-flex items-center text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t("admin.back_to_list")}
          </Link>
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-50 sm:text-3xl">
              {t("admin.bulk_title")}
            </h1>
            <Button variant="outline" size="sm" onClick={handleJsonImport}>
              <Upload className="mr-1.5 h-4 w-4" />
              {t("admin.bulk_import_json")}
            </Button>
          </div>
          <p className="mt-1 text-sm text-zinc-500">
            {t("admin.bulk_desc")} ({questions.length}{t("common.count_suffix")})
          </p>
        </div>

        {/* Result */}
        {result && (
          <Card className="mb-6 border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
            <CardContent className="flex items-center gap-3 py-4">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <div>
                <p className="font-medium text-green-700 dark:text-green-300">
                  {result.created}{t("admin.bulk_success")}
                  {result.failed > 0 && `, ${result.failed}${t("admin.bulk_failed")}`}
                </p>
                {result.errors && result.errors.length > 0 && (
                  <ul className="mt-1 text-xs text-green-600 dark:text-green-400">
                    {result.errors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error */}
        {error && (
          <div className="mb-6 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Questions */}
        <div className="space-y-4">
          {questions.map((q, qIdx) => (
            <Card key={qIdx}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle
                    className="flex cursor-pointer items-center gap-2 text-sm"
                    onClick={() => toggleCollapse(qIdx)}
                  >
                    {q.collapsed ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronUp className="h-4 w-4" />
                    )}
                    <Badge variant="secondary" className="text-xs">
                      {qIdx + 1}
                    </Badge>
                    {q.question_number
                      ? `${t("questions.question_num")}${q.question_number}`
                      : t("admin.bulk_new")}
                    {q.text_ko && (
                      <span className="ml-2 max-w-[200px] truncate text-xs font-normal text-zinc-400">
                        {q.text_ko}
                      </span>
                    )}
                  </CardTitle>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700"
                    onClick={() => removeQuestion(qIdx)}
                    disabled={questions.length <= 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>

              {!q.collapsed && (
                <CardContent className="space-y-4">
                  {/* Basic info */}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="space-y-1">
                      <Label className="text-xs">{t("admin.question_number")}</Label>
                      <Input
                        type="number"
                        placeholder={t("admin.number_placeholder")}
                        value={q.question_number}
                        onChange={(e) =>
                          updateQuestion(qIdx, "question_number", e.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t("common.category")}</Label>
                      <Select
                        value={q.category_id || "none"}
                        onValueChange={(v) =>
                          updateQuestion(
                            qIdx,
                            "category_id",
                            !v || v === "none" ? "" : String(v)
                          )
                        }
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t("admin.select_category_option")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t("admin.no_category")}</SelectItem>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={String(cat.id)}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end gap-2 pb-1">
                      <input
                        type="checkbox"
                        checked={q.is_multiple}
                        onChange={(e) =>
                          updateQuestion(qIdx, "is_multiple", e.target.checked)
                        }
                        className="h-4 w-4 rounded border-zinc-300 accent-primary"
                        id={`multi-${qIdx}`}
                      />
                      <Label htmlFor={`multi-${qIdx}`} className="text-xs">
                        {t("admin.multiple_answer")}
                      </Label>
                    </div>
                  </div>

                  {/* Question text */}
                  <div className="space-y-1">
                    <Label className="text-xs">{t("admin.question_text")} *</Label>
                    <textarea
                      rows={3}
                      placeholder={t("admin.text_ko_placeholder")}
                      value={q.text_ko}
                      onChange={(e) =>
                        updateQuestion(qIdx, "text_ko", e.target.value)
                      }
                      className="w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 py-2 text-sm transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 dark:bg-input/30"
                    />
                  </div>

                  {/* Choices */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">{t("common.choices")}</Label>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs"
                          onClick={() => removeChoice(qIdx)}
                          disabled={q.choices.length <= 2}
                        >
                          {t("common.delete")}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs"
                          onClick={() => addChoice(qIdx)}
                          disabled={q.choices.length >= 5}
                        >
                          {t("common.add")}
                        </Button>
                      </div>
                    </div>
                    {q.choices.map((c, cIdx) => (
                      <div
                        key={c.label}
                        className="flex items-center gap-2"
                      >
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-zinc-100 text-xs font-bold text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                          {c.label}
                        </span>
                        <Input
                          placeholder={`${t("admin.choice_placeholder")} ${c.label}`}
                          value={c.text}
                          onChange={(e) =>
                            updateChoice(qIdx, cIdx, "text", e.target.value)
                          }
                          className="flex-1 h-8 text-sm"
                        />
                        <div className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            checked={c.is_correct}
                            onChange={(e) =>
                              updateChoice(
                                qIdx,
                                cIdx,
                                "is_correct",
                                e.target.checked
                              )
                            }
                            className="h-4 w-4 rounded border-zinc-300 accent-primary"
                            id={`correct-${qIdx}-${c.label}`}
                          />
                          <Label
                            htmlFor={`correct-${qIdx}-${c.label}`}
                            className="text-xs whitespace-nowrap"
                          >
                            {t("common.correct")}
                          </Label>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        {/* Add question button */}
        <Button
          variant="outline"
          className="mt-4 w-full border-dashed"
          onClick={addQuestion}
        >
          <Plus className="mr-1.5 h-4 w-4" />
          {t("admin.bulk_add")}
        </Button>

        {/* Submit */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
          <Link
            to="/exams/$slug/admin/questions"
            params={{ slug: examSlug }}
          >
            <Button type="button" variant="outline" className="w-full sm:w-auto">
              {t("common.cancel")}
            </Button>
          </Link>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-1.5 h-4 w-4" />
            )}
            {questions.length}{t("common.count_suffix")} {t("admin.bulk_submit")}
          </Button>
        </div>
      </div>
    </div>
  );
}
